import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
    type RefObject,
} from 'react';
import { A4_PAGE_WIDTH } from '@/constants';
import { useIsMobile } from '@/hooks/use-mobile';
import { useZoom } from '../context/ZoomContext';
import { useReportZoom } from '../hooks/useReportZoom';

interface ReportZoomSurfaceProps {
    scrollContainerRef: RefObject<HTMLDivElement>;
    children: ReactNode;
}

/**
 * Applies the preview zoom via `transform: scale` (GPU-composited, no full
 * document reflow) instead of the CSS `zoom` property. Because `transform` does
 * not affect the layout box, an outer sizing box reserves the scaled footprint
 * so the scroll container and horizontal centering behave as before.
 *
 * `children` (the ReportPreview element) is created by the non-zoom-consuming
 * parent, so a zoom change re-renders only this component — React bails out of
 * reconciling the (referentially stable) children subtree.
 */
export function ReportZoomSurface({ scrollContainerRef, children }: ReportZoomSurfaceProps) {
    const isMobile = useIsMobile();
    const { zoom, setZoom } = useZoom();
    const [mobileFitZoom, setMobileFitZoom] = useState(1);
    const contentRef = useRef<HTMLDivElement>(null);
    const [naturalHeight, setNaturalHeight] = useState(0);

    useReportZoom(setZoom);

    // Fit-to-width zoom on mobile, tracking the scroll container's width.
    useEffect(() => {
        if (!isMobile) {
            setMobileFitZoom(1);
            return;
        }

        const container = scrollContainerRef.current;
        if (!container) return;

        const updateMobileFitZoom = () => {
            const availableWidth = Math.max(container.clientWidth - 24, 0);
            const nextZoom = Math.min(1, Math.max(0.3, availableWidth / A4_PAGE_WIDTH));
            setMobileFitZoom((prev) => (Math.abs(prev - nextZoom) < 0.005 ? prev : nextZoom));
        };

        updateMobileFitZoom();

        const observer = new ResizeObserver(updateMobileFitZoom);
        observer.observe(container);
        window.addEventListener('resize', updateMobileFitZoom);
        window.addEventListener('orientationchange', updateMobileFitZoom);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateMobileFitZoom);
            window.removeEventListener('orientationchange', updateMobileFitZoom);
        };
    }, [isMobile, scrollContainerRef]);

    // Track the unscaled content height so the outer box can reserve the scaled
    // footprint. `transform` does not affect `offsetHeight`, and does not fire the
    // ResizeObserver, so this recomputes only when the report content changes.
    useLayoutEffect(() => {
        const element = contentRef.current;
        if (!element) return;

        const measure = () => setNaturalHeight(element.offsetHeight);
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const effectiveZoom = isMobile ? mobileFitZoom : zoom;

    return (
        <div className="relative flex justify-center p-3 md:p-8">
            <div
                style={{
                    width: A4_PAGE_WIDTH * effectiveZoom,
                    height: naturalHeight ? naturalHeight * effectiveZoom : undefined,
                }}
            >
                <div
                    ref={contentRef}
                    data-report-zoom={effectiveZoom}
                    style={{
                        width: A4_PAGE_WIDTH,
                        transform: `scale(${effectiveZoom})`,
                        transformOrigin: 'top left',
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
